import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, Text } from '@fluentui/react-components';
import type { Incident } from 'types';
import { IncidentState, IncidentSeverity } from 'types';
import { useTick } from 'hooks';
import { SOC_COLORS } from 'themes';
import { IncidentTagBadges } from './IncidentTagBadges';

interface WallScreenIncidentListProps {
  incidents: Incident[];
  isLoading: boolean;
  error: string | null;
}

const FRESH_DURATION_MS = 2 * 60 * 1000; // 2 minutes

type SeverityBucket = 'high' | 'medium' | 'lowInfo';

const COLUMN_CONFIG: Record<SeverityBucket, { label: string; color: string; bg: string }> = {
  high: { label: 'HIGH', color: '#e8394a', bg: 'rgba(232,57,74,0.08)' },
  medium: { label: 'MEDIUM', color: '#ffcb05', bg: 'rgba(255,203,5,0.06)' },
  lowInfo: { label: 'LOW / INFO', color: '#8dc63f', bg: 'rgba(141,198,63,0.05)' },
};

function severityBucket(severity: IncidentSeverity): SeverityBucket {
  if (severity === IncidentSeverity.High) return 'high';
  if (severity === IncidentSeverity.Medium) return 'medium';
  return 'lowInfo';
}

function formatSLA(seconds: number): string {
  const neg = seconds < 0;
  const a = Math.abs(seconds);
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  const sec = a % 60;
  const sign = neg ? '−' : '';
  return h > 0
    ? `${sign}${h}h ${String(m).padStart(2, '0')}m`
    : `${sign}${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function slaColor(seconds: number): string {
  if (seconds < 0) return SOC_COLORS.red;
  if (seconds < 600) return SOC_COLORS.orange;
  return SOC_COLORS.textMid;
}

function getSlaSeconds(slaDeadline: string): number {
  return Math.floor((new Date(slaDeadline).getTime() - Date.now()) / 1000);
}

function isFreshIncident(incident: Incident): boolean {
  // Fresh if: created within last 2 minutes AND no assigned owner
  if (incident.assignedOperatorId) return false;
  const createdTime = new Date(incident.createdTime).getTime();
  return Date.now() - createdTime < FRESH_DURATION_MS;
}

interface IncidentCardProps {
  incident: Incident;
  columnColor: string;
}

const IncidentCard: React.FC<IncidentCardProps> = ({ incident, columnColor }) => {
  const slaSeconds = getSlaSeconds(incident.slaDeadline);
  const isClosed = incident.state === IncidentState.Closed;
  const isOverdue = slaSeconds < 0 && !isClosed;
  const isFresh = isFreshIncident(incident);

  return (
    <div
      style={{
        background: isFresh ? 'rgba(0,255,255,0.06)' : isClosed ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.03)',
        borderLeft: isFresh ? '4px solid #00ffff' : `3px solid ${isClosed ? 'transparent' : columnColor}`,
        borderBottom: `1px solid ${SOC_COLORS.border}`,
        opacity: isClosed ? 0.4 : 1,
        padding: '12px 14px',
        animation: isFresh ? 'freshGlow 1.2s ease-in-out infinite' :
                   isOverdue ? 'cardPulse 2.5s ease-in-out infinite' : 'none',
        position: 'relative',
      }}
    >
      {/* Fresh indicator badge */}
      {isFresh && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 12,
            fontWeight: 700,
            color: '#00ffff',
            background: 'rgba(0, 255, 255, 0.15)',
            border: '2px solid rgba(0, 255, 255, 0.6)',
            borderRadius: 3,
            padding: '4px 10px',
            letterSpacing: 2,
            animation: 'freshBadgePulse 0.8s ease-in-out infinite',
            textShadow: '0 0 8px rgba(0, 255, 255, 0.8)',
          }}
        >
          NEW
        </div>
      )}

      {/* Title */}
      <div
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: 20,
          fontWeight: 700,
          color: isClosed ? SOC_COLORS.textDim : SOC_COLORS.textPrimary,
          letterSpacing: 0.2,
          lineHeight: 1.25,
          marginBottom: 6,
          paddingRight: isFresh ? 40 : 0,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {incident.title}
      </div>

      {/* ID */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 12, color: SOC_COLORS.teal }}>
          {incident.incidentId}
        </span>
        <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 12, color: SOC_COLORS.textDim }}>
          CUST: {incident.customerId}
        </span>
      </div>

      {/* Tags (Critical / NIS2) */}
      {(incident.tags?.isCritical || (incident.tags?.nis2Categories?.length ?? 0) > 0) && (
        <div style={{ marginBottom: 10 }}>
          <IncidentTagBadges tags={incident.tags} />
        </div>
      )}

      {/* SLA and Owner row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 22,
            color: isClosed ? SOC_COLORS.textDim : slaColor(slaSeconds),
            letterSpacing: 1,
            lineHeight: 1,
            textShadow: isOverdue ? `0 0 8px ${SOC_COLORS.red}88` : 'none',
          }}
        >
          {formatSLA(slaSeconds)}
        </div>
        <div
          style={{
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 12,
            color: incident.assignedOperatorId ? SOC_COLORS.green : SOC_COLORS.orange,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '50%',
          }}
        >
          {incident.assignedOperatorName || (incident.assignedOperatorId ? incident.assignedOperatorId : 'UNASSIGNED')}
        </div>
      </div>
    </div>
  );
};

interface ColumnProps {
  title: string;
  color: string;
  bg: string;
  incidents: Incident[];
}

const Column: React.FC<ColumnProps> = ({ title, color, bg, incidents }) => {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: bg,
        borderRadius: 4,
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      {/* Column Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          borderBottom: `2px solid ${color}`,
          background: `${color}11`,
        }}
      >
        <span
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 22,
            fontWeight: 700,
            color: color,
            letterSpacing: 2,
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 26,
            color: color,
            lineHeight: 1,
          }}
        >
          {incidents.length}
        </span>
      </div>

      {/* Incident Cards */}
      <div
        style={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
        className="hide-scrollbar"
      >
        {incidents.map((inc) => (
          <IncidentCard key={inc.incidentId} incident={inc} columnColor={color} />
        ))}
        {incidents.length === 0 && (
          <div
            style={{
              padding: '24px 14px',
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: 14,
              color: SOC_COLORS.textDim,
              textAlign: 'center',
            }}
          >
            NO INCIDENTS
          </div>
        )}
      </div>
    </div>
  );
};

export const WallScreenIncidentList: React.FC<WallScreenIncidentListProps> = ({
  incidents,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  // Force re-render every second for live SLA updates and fresh indicator
  useTick(1000);

  if (isLoading && incidents.length === 0) {
    return <Spinner label={t('common.loading')} size="large" style={{ color: SOC_COLORS.teal }} />;
  }

  if (error) {
    return (
      <div style={{ color: SOC_COLORS.red, fontFamily: "'Share Tech Mono', monospace", padding: 20 }}>
        {error}
      </div>
    );
  }

  // Filter out closed incidents for wall-screen mode
  const activeIncidents = incidents.filter((i) => i.state !== IncidentState.Closed);

  // Sort: fresh new > overdue > sorted by received time (newest first)
  const sortIncidents = (a: Incident, b: Incident) => {
    const now = Date.now();
    const aFresh = isFreshIncident(a);
    const bFresh = isFreshIncident(b);
    const aOverdue = new Date(a.slaDeadline).getTime() < now;
    const bOverdue = new Date(b.slaDeadline).getTime() < now;

    if (aFresh !== bFresh) return aFresh ? -1 : 1;
    if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
    return new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime();
  };

  // Bucket incidents by severity column, then sort each column
  const buckets: Record<SeverityBucket, Incident[]> = { high: [], medium: [], lowInfo: [] };
  for (const inc of activeIncidents) {
    buckets[severityBucket(inc.severity)].push(inc);
  }
  (Object.keys(buckets) as SeverityBucket[]).forEach((key) => buckets[key].sort(sortIncidents));

  if (activeIncidents.length === 0) {
    return (
      <Text
        align="center"
        block
        style={{ 
          color: SOC_COLORS.textDim, 
          fontFamily: "'Barlow Condensed', sans-serif", 
          fontSize: 24,
          padding: '40px 0',
        }}
      >
        {t('statusPanel.noIncidents')}
      </Text>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 12, height: '100%', minHeight: 0 }}>
      {(Object.keys(COLUMN_CONFIG) as SeverityBucket[]).map((key) => {
        const cfg = COLUMN_CONFIG[key];
        return (
          <Column
            key={key}
            title={cfg.label}
            color={cfg.color}
            bg={cfg.bg}
            incidents={buckets[key]}
          />
        );
      })}

      {/* CSS Animations */}
      <style>{`
        @keyframes cardPulse {
          0%,100% { background: rgba(232,57,74,0.08); }
          50%     { background: rgba(232,57,74,0.20); }
        }
        @keyframes freshGlow {
          0%,100% { 
            box-shadow: inset 0 0 0 2px rgba(0,255,255,0.4), 0 0 15px rgba(0,255,255,0.2);
            background: rgba(0,255,255,0.05);
          }
          50% { 
            box-shadow: inset 0 0 0 2px rgba(0,255,255,0.8), 0 0 25px rgba(0,255,255,0.4), inset 0 0 20px rgba(0,255,255,0.1);
            background: rgba(0,255,255,0.1);
          }
        }
        @keyframes freshBadgePulse {
          0%,100% { 
            opacity: 1;
            transform: scale(1);
            box-shadow: 0 0 10px rgba(0,255,255,0.5);
          }
          50% { 
            opacity: 0.8;
            transform: scale(1.05);
            box-shadow: 0 0 20px rgba(0,255,255,0.8);
          }
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};
